// Licencas.js - Componente de Gestão de Licenças (CORRIGIDO)
// ✅ NUNCA CRIA TRIAL AUTOMATICAMENTE
// ✅ NUNCA APAGA LICENÇAS EXPIRADAS — APENAS DESATIVA
// ✅ ALINHADO COM routes/licencas.js e models/Licenca.js
// 🔥 NOVO: Suporte para campo `expirada`
// 🔥 NOVO: Suporte para campo `motivoInativacao`
// 🔥 NOVO: Suporte para campo `dataInativacao`
// 🔥 NOVO: Suporte para campo `dataExpiracaoAnterior` (histórico)
// 🔥 NOVO: Estatística separada de "Expiradas" vs "Inativas"
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Licencas.css';
import { Icon } from './SharedComponents';

const API_BASE_URL = '/api';

export default function Licencas() {
    // ==============================================
    // ESTADOS
    // ==============================================
    const [licencas, setLicencas] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLicenca, setSelectedLicenca] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [tempToast, setTempToast] = useState(null);
    const [isSuperadmin, setIsSuperadmin] = useState(false);
    const [loggedUser, setLoggedUser] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        ativas: 0,
        expiradas: 0,
        pendentes: 0,
        inativas: 0,
        empresa_inexistente: 0,
        sem_licenca: 0
    });

    // ==============================================
    // ESTADOS DE PAGINAÇÃO
    // ==============================================
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    // ==============================================
    // ESTADO DO FORMULÁRIO
    // ==============================================
    const [formData, setFormData] = useState({
        empresaId: '',
        chave: '',
        tipoPrazo: 'Mensal',
        dataInicio: '',
        dataFim: '',
        dataExpiracao: '',
        plano: 'Basico',
        limiteUtilizadores: 5,
        ativa: true
    });

    // ==============================================
    // ESTADO PARA RENOVAÇÃO
    // ==============================================
    const [showRenovarModal, setShowRenovarModal] = useState(false);
    const [licencaToRenovar, setLicencaToRenovar] = useState(null);
    const [renovando, setRenovando] = useState(false);
    const [renovacaoData, setRenovacaoData] = useState({
        periodo: 'Mensal',
        dias: 30
    });

    // ==============================================
    // ESTADO PARA APAGAR
    // ==============================================
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [licencaToDelete, setLicencaToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // ==============================================
    // FUNÇÕES AUXILIARES
    // ==============================================
    const showTempToast = (title, message, type = 'success') => {
        setTempToast({ title, message, type });
        setTimeout(() => setTempToast(null), 3000);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleString('pt-PT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleString('pt-PT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // ==============================================
    // 🔥 VERIFICAR SE A EMPRESA EXISTE
    // ==============================================
    const verificarEmpresaExiste = (licenca) => {
        if (!licenca) return false;
        const empresaId = licenca.empresaId?._id || licenca.empresaId;
        if (!empresaId) return false;

        const empresa = empresas.find(e => e._id?.toString() === empresaId.toString());
        return !!empresa;
    };

    // ==============================================
    // 🔥 FUNÇÃO GET STATUS LABEL - CORRIGIDA
    // ==============================================
    const getStatusLabel = (licenca) => {
        if (!licenca) return 'Sem Licença';

        // 🔥 PRIORIDADE 1: Verificar se a empresa existe
        if (!verificarEmpresaExiste(licenca)) {
            return 'Empresa Inexistente';
        }

        // 🔥 PRIORIDADE 2: Verificar se a empresa está arquivada ou inativa
        const empresaId = licenca.empresaId?._id || licenca.empresaId;
        const empresa = empresas.find(e => e._id?.toString() === empresaId?.toString());

        if (empresa) {
            if (empresa.arquivado === true) return 'Empresa Arquivada';
            if (empresa.ativa === false) return 'Empresa Inativa';
        }

        // 🔥 PRIORIDADE 3: Licença arquivada
        if (licenca.arquivado === true) return 'Arquivada';

        // 🔥 PRIORIDADE 4: Licença marcada como expirada (novo campo)
        if (licenca.expirada === true) return 'Expirada';

        // 🔥 PRIORIDADE 5: Licença inativa
        if (!licenca.ativa) {
            // Distinguir entre expirada e desativada manualmente
            if (licenca.motivoInativacao === 'Expirada') return 'Expirada';
            if (licenca.motivoInativacao === 'Desativada manualmente') return 'Desativada';
            return 'Inativa';
        }

        // 🔥 PRIORIDADE 6: Licença desativada pelo tipoPrazo
        if (licenca.tipoPrazo === 'Desativada') return 'Desativada';

        // 🔥 PRIORIDADE 7: Licença infinita
        if (licenca.tipoPrazo === 'Infinito') return 'Ativa (∞)';

        // 🔥 PRIORIDADE 8: Verificar expiração pela data
        const dataFim = licenca.dataExpiracao || licenca.dataFim;

        if (dataFim) {
            const hoje = new Date();
            const fim = new Date(dataFim);
            hoje.setHours(0, 0, 0, 0);
            fim.setHours(0, 0, 0, 0);
            if (fim < hoje) return 'Expirada';
            const diff = fim - hoje;
            const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
            if (dias <= 7) return 'A expirar';
        }

        return 'Ativa';
    };

    // ==============================================
    // 🔥 FUNÇÃO GET STATUS BADGE CLASS
    // ==============================================
    const getStatusBadgeClass = (licenca) => {
        const status = getStatusLabel(licenca);
        switch (status) {
            case 'Ativa': return 'status-ativa';
            case 'Ativa (∞)': return 'status-ativa';
            case 'A expirar': return 'status-a-expirar';
            case 'Expirada': return 'status-expirada';
            case 'Inativa': return 'status-inativa';
            case 'Desativada': return 'status-desativada';
            case 'Arquivada': return 'status-arquivada';
            case 'Sem Licença': return 'status-sem-licenca';
            case 'Empresa Inexistente': return 'status-empresa-inexistente';
            case 'Empresa Arquivada': return 'status-empresa-arquivada';
            case 'Empresa Inativa': return 'status-empresa-inativa';
            default: return 'status-inativa';
        }
    };

    const getPlanoBadge = (plano) => {
        const planoConfig = {
            'Trial': { class: 'plano-trial', icon: 'science' },
            'Basico': { class: 'plano-basico', icon: 'star' },
            'Profissional': { class: 'plano-profissional', icon: 'stars' },
            'Enterprise': { class: 'plano-enterprise', icon: 'workspace_premium' },
            'Custom': { class: 'plano-personalizado', icon: 'build' }
        };
        const config = planoConfig[plano] || planoConfig['Basico'];
        return (
            <span className={`plano-badge ${config.class}`}>
                <Icon name={config.icon} size={12} /> {plano || 'Basico'}
            </span>
        );
    };

    const getTipoPrazoLabel = (tipo) => {
        const tipos = {
            'Infinito': '♾️ Infinito',
            'DataFim': '📅 Data Específica',
            'Mensal': '📆 Mensal',
            'Anual': '📅 Anual',
            'Desativada': '🔴 Desativada'
        };
        return tipos[tipo] || tipo;
    };

    // ==============================================
    // HELPERS DE EMPRESA
    // ==============================================
    const extrairEmpresaId = (empresa) => {
        if (!empresa) return null;
        if (typeof empresa === 'string') return empresa;
        if (typeof empresa === 'object' && empresa._id) return empresa._id;
        if (typeof empresa === 'object' && empresa.id) return empresa.id;
        return null;
    };

    const getEmpresaNome = (licenca) => {
        if (!licenca) return 'Empresa';
        if (licenca.empresa?.nome) return licenca.empresa.nome;
        if (licenca.empresaNome) return licenca.empresaNome;

        const empresaId = extrairEmpresaId(licenca.empresaId) || extrairEmpresaId(licenca.empresa);
        if (empresaId) {
            const empresaEncontrada = empresas.find(e => e._id?.toString() === empresaId.toString());
            if (empresaEncontrada) return empresaEncontrada.nome;
        }
        return 'Empresa Desconhecida';
    };

    const extrairEmpresaIdDaLicenca = (licenca) => {
        if (!licenca) return '';
        const id = extrairEmpresaId(licenca.empresaId);
        if (id) return id;
        const id2 = extrairEmpresaId(licenca.empresa);
        if (id2) return id2;
        return '';
    };

    // ==============================================
    // 🔥 GERAR CHAVE DE LICENÇA
    // ==============================================
    const generateLicencaKey = () => {
        const prefix = 'LIC';
        const random = Math.random().toString(36).substring(2, 16).toUpperCase();
        const ano = new Date().getFullYear();
        return `${prefix}-${random}-${ano}`;
    };

    // ==============================================
    // 🔥 CALCULAR STATUS DIRETO (USADO NO STATS)
    // ==============================================
    const calcularStatusDireto = (licenca, empresasList) => {
        if (!licenca) return 'Sem Licença';

        const empresaId = licenca.empresaId?._id || licenca.empresaId;
        const empresa = empresasList.find(e => e._id?.toString() === empresaId?.toString());

        if (!empresa) return 'Empresa Inexistente';

        if (empresa.arquivado === true) return 'Empresa Arquivada';
        if (empresa.ativa === false) return 'Empresa Inativa';
        if (licenca.arquivado === true) return 'Arquivada';

        // 🔥 Nova prioridade: campo expirada
        if (licenca.expirada === true) return 'Expirada';

        if (!licenca.ativa) {
            if (licenca.motivoInativacao === 'Expirada') return 'Expirada';
            if (licenca.motivoInativacao === 'Desativada manualmente') return 'Desativada';
            return 'Inativa';
        }

        if (licenca.tipoPrazo === 'Desativada') return 'Desativada';
        if (licenca.tipoPrazo === 'Infinito') return 'Ativa (∞)';

        const dataFim = licenca.dataExpiracao || licenca.dataFim;
        if (dataFim) {
            const hoje = new Date();
            const fim = new Date(dataFim);
            hoje.setHours(0, 0, 0, 0);
            fim.setHours(0, 0, 0, 0);
            if (fim < hoje) return 'Expirada';
            const diff = fim - hoje;
            const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
            if (dias <= 7) return 'A expirar';
        }

        return 'Ativa';
    };

    // ==============================================
    // BUSCAR DADOS
    // ==============================================
    const fetchLicencas = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            const response = await axios.get(`${API_BASE_URL}/licencas`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                const licencasData = response.data.licencas || response.data.data || [];
                setLicencas(licencasData);
                calcularStats(licencasData, empresas);
                setCurrentPage(1);
            } else {
                setLicencas([]);
            }
        } catch (err) {
            console.error('❌ Erro ao buscar licenças:', err);
            if (err.response?.status === 403) {
                showTempToast('Acesso Negado', 'Não tem permissão para visualizar licenças', 'danger');
            } else {
                showTempToast('Erro', 'Não foi possível carregar as licenças', 'danger');
            }
            setLicencas([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchEmpresas = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return [];

            const response = await axios.get(`${API_BASE_URL}/empresas`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                const empresasData = response.data.data || [];
                setEmpresas(empresasData);
                console.log('🏢 Empresas carregadas:', empresasData.length);

                if (licencas.length > 0) {
                    calcularStats(licencas, empresasData);
                }
                return empresasData;
            }
        } catch (err) {
            console.error('❌ Erro ao buscar empresas:', err);
        }
        return [];
    };

    const fetchLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return null;

            const response = await axios.get(`${API_BASE_URL}/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                const userData = response.data.data;
                setLoggedUser(userData);
                setIsSuperadmin(userData.perfil === 'Superadmin');
                return userData;
            }
        } catch (err) {
            console.error('❌ Erro ao buscar utilizador logado:', err);
            const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
            if (storedUser && storedUser._id) {
                setLoggedUser(storedUser);
                setIsSuperadmin(storedUser.perfil === 'Superadmin');
                return storedUser;
            }
        }
        return null;
    };

    // ==============================================
    // 🔥 CALCULAR ESTATÍSTICAS
    // ==============================================
    const calcularStats = (licencasData, empresasList) => {
        const empresasParaUsar = empresasList || empresas;

        const stats = {
            total: 0,
            ativas: 0,
            expiradas: 0,
            pendentes: 0,
            inativas: 0,
            empresa_inexistente: 0,
            sem_licenca: 0
        };

        licencasData.forEach(lic => {
            const status = calcularStatusDireto(lic, empresasParaUsar);

            switch (status) {
                case 'Ativa':
                case 'Ativa (∞)':
                    stats.ativas++;
                    break;
                case 'A expirar':
                    stats.pendentes++;
                    break;
                case 'Expirada':
                    stats.expiradas++;
                    break;
                case 'Inativa':
                case 'Desativada':
                case 'Arquivada':
                case 'Empresa Arquivada':
                case 'Empresa Inativa':
                    stats.inativas++;
                    break;
                case 'Empresa Inexistente':
                    stats.empresa_inexistente++;
                    stats.inativas++;
                    break;
                default:
                    stats.inativas++;
            }
        });

        stats.total = licencasData.length;

        // 🔥 Contar empresas SEM licença
        empresasParaUsar.forEach(emp => {
            const temLicenca = licencasData.some(l => {
                const empresaId = l.empresaId?._id || l.empresaId;
                return empresaId?.toString() === emp._id?.toString();
            });
            if (!temLicenca) {
                stats.sem_licenca++;
                stats.total++;
            }
        });

        setStats(stats);
    };

    // ==============================================
    // CARREGAR DADOS INICIAIS
    // ==============================================
    useEffect(() => {
        const carregarDados = async () => {
            const user = await fetchLoggedUser();
            if (user && (user.perfil === 'Superadmin' || user.perfil === 'Admin')) {
                await fetchEmpresas();
                await fetchLicencas();
            }
        };
        carregarDados();
    }, []);

    // ==============================================
    // RECALCULAR ESTATÍSTICAS QUANDO MUDAREM
    // ==============================================
    useEffect(() => {
        if (empresas.length > 0) {
            calcularStats(licencas, empresas);
        }
    }, [licencas, empresas]);

    // ==============================================
    // FILTRAR LICENÇAS
    // ==============================================
    const filteredLicencas = licencas.filter(lic => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase().trim();
        const empresaNome = getEmpresaNome(lic);
        return empresaNome.toLowerCase().includes(term) ||
            lic.chave?.toLowerCase().includes(term) ||
            lic.plano?.toLowerCase().includes(term) ||
            lic.tipoPrazo?.toLowerCase().includes(term) ||
            lic.motivoInativacao?.toLowerCase().includes(term);
    });

    // ==============================================
    // PAGINAÇÃO
    // ==============================================
    useEffect(() => {
        const total = Math.ceil(filteredLicencas.length / itemsPerPage);
        setTotalPages(total > 0 ? total : 1);
        if (currentPage > total && total > 0) {
            setCurrentPage(1);
        }
    }, [filteredLicencas, itemsPerPage]);

    const getCurrentPageLicencas = () => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredLicencas.slice(startIndex, endIndex);
    };

    const goToPage = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    // ==============================================
    // ABRIR MODAL DE CRIAÇÃO
    // ==============================================
    const openAddModal = () => {
        setSelectedLicenca(null);
        setIsEditing(false);
        setFormData({
            empresaId: '',
            chave: generateLicencaKey(),
            tipoPrazo: 'Mensal',
            dataInicio: new Date().toISOString().split('T')[0],
            dataFim: '',
            dataExpiracao: '',
            plano: 'Basico',
            limiteUtilizadores: 5,
            ativa: true
        });
        setShowModal(true);
    };

    // ==============================================
    // ABRIR MODAL DE EDIÇÃO
    // ==============================================
    const openEditModal = (licenca) => {
        setSelectedLicenca(licenca);
        setIsEditing(true);

        const empresaId = extrairEmpresaIdDaLicenca(licenca);

        setFormData({
            empresaId: empresaId,
            chave: licenca.chave || generateLicencaKey(),
            tipoPrazo: licenca.tipoPrazo || 'Mensal',
            dataInicio: licenca.dataInicio ? new Date(licenca.dataInicio).toISOString().split('T')[0] : '',
            dataFim: licenca.dataFim ? new Date(licenca.dataFim).toISOString().split('T')[0] : '',
            dataExpiracao: licenca.dataExpiracao ? new Date(licenca.dataExpiracao).toISOString().split('T')[0] : '',
            plano: licenca.plano || 'Basico',
            limiteUtilizadores: licenca.limiteUtilizadores || 5,
            ativa: licenca.ativa !== false
        });

        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedLicenca(null);
        setIsEditing(false);
    };

    // ==============================================
    // ABRIR MODAL DE RENOVAÇÃO
    // ==============================================
    const openRenovarModal = (licenca) => {
        setLicencaToRenovar(licenca);
        setRenovacaoData({
            periodo: 'Mensal',
            dias: 30
        });
        setShowRenovarModal(true);
    };

    const closeRenovarModal = () => {
        setShowRenovarModal(false);
        setLicencaToRenovar(null);
        setRenovando(false);
    };

    // ==============================================
    // RENOVAR LICENÇA
    // ==============================================
    const renovarLicenca = async () => {
        if (!licencaToRenovar) return;
        setRenovando(true);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showTempToast('Erro', 'Sessão expirada', 'danger');
                setRenovando(false);
                return;
            }

            if (!verificarEmpresaExiste(licencaToRenovar)) {
                showTempToast('Erro', 'Não é possível renovar uma licença de uma empresa que não existe', 'danger');
                setRenovando(false);
                return;
            }

            const data = {
                periodo: renovacaoData.periodo,
                dias: renovacaoData.dias
            };

            const response = await axios.put(
                `${API_BASE_URL}/licencas/${licencaToRenovar._id}/renovar`,
                data,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data && response.data.success) {
                showTempToast('Sucesso', response.data.mensagem || 'Licença renovada com sucesso!', 'success');
                closeRenovarModal();
                await fetchLicencas();
            } else {
                showTempToast('Erro', response.data?.erro || 'Erro ao renovar licença', 'danger');
            }

        } catch (err) {
            console.error('❌ Erro ao renovar licença:', err);
            const msg = err.response?.data?.erro || err.response?.data?.message || 'Erro ao renovar licença';
            showTempToast('Erro', msg, 'danger');
        } finally {
            setRenovando(false);
        }
    };

    // ==============================================
    // SALVAR LICENÇA
    // ==============================================
    const saveLicenca = async () => {
        if (!formData.empresaId) {
            showTempToast('Erro', 'Selecione uma empresa', 'danger');
            return;
        }
        if (!formData.dataInicio) {
            showTempToast('Erro', 'Selecione a data de início', 'danger');
            return;
        }

        const empresaExiste = empresas.some(e => e._id?.toString() === formData.empresaId.toString());
        if (!empresaExiste) {
            showTempToast('Erro', 'A empresa selecionada não existe no sistema', 'danger');
            return;
        }

        const dataInicio = new Date(formData.dataInicio);
        const dataFim = formData.dataFim ? new Date(formData.dataFim) : null;

        if (formData.tipoPrazo === 'DataFim' && dataFim && dataFim <= dataInicio) {
            showTempToast('Erro', 'A data de fim deve ser posterior à data de início', 'danger');
            return;
        }

        if (formData.limiteUtilizadores < 1) {
            showTempToast('Erro', 'O limite de utilizadores deve ser no mínimo 1', 'danger');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showTempToast('Erro', 'Sessão expirada', 'danger');
                return;
            }

            const data = {
                empresaId: formData.empresaId,
                chave: formData.chave,
                tipoPrazo: formData.tipoPrazo,
                dataInicio: new Date(formData.dataInicio).toISOString(),
                dataFim: dataFim ? dataFim.toISOString() : null,
                plano: formData.plano,
                limiteUtilizadores: Number(formData.limiteUtilizadores),
                ativa: formData.ativa
            };

            if (isEditing && selectedLicenca) {
                await axios.put(
                    `${API_BASE_URL}/licencas/${selectedLicenca._id}`,
                    data,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showTempToast('Sucesso', 'Licença atualizada com sucesso!', 'success');
            } else {
                await axios.post(
                    `${API_BASE_URL}/licencas`,
                    data,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showTempToast('Sucesso', 'Licença criada com sucesso!', 'success');
            }

            closeModal();
            await fetchLicencas();

        } catch (err) {
            console.error('❌ Erro ao salvar licença:', err);
            const msg = err.response?.data?.erro || err.response?.data?.message || 'Erro ao salvar licença';
            showTempToast('Erro', msg, 'danger');
        }
    };

    // ==============================================
    // ALTERNAR ESTADO DA LICENÇA
    // ==============================================
    const toggleLicencaStatus = async (licenca) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showTempToast('Erro', 'Sessão expirada', 'danger');
                return;
            }

            if (!verificarEmpresaExiste(licenca)) {
                showTempToast('Erro', 'Não é possível alterar o estado de uma licença de uma empresa que não existe', 'danger');
                return;
            }

            const novoEstado = !licenca.ativa;

            if (novoEstado) {
                await axios.put(
                    `${API_BASE_URL}/licencas/${licenca._id}/ativar`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showTempToast('Sucesso', 'Licença ativada com sucesso!', 'success');
            } else {
                await axios.put(
                    `${API_BASE_URL}/licencas/${licenca._id}/desativar`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                showTempToast('Sucesso', 'Licença desativada com sucesso!', 'success');
            }

            await fetchLicencas();

        } catch (err) {
            console.error('❌ Erro ao alternar estado:', err);
            const msg = err.response?.data?.erro || err.response?.data?.message || 'Erro ao alternar estado';
            showTempToast('Erro', msg, 'danger');
        }
    };

    // ==============================================
    // APAGAR LICENÇA
    // ==============================================
    const confirmDelete = (licenca) => {
        setLicencaToDelete(licenca);
        setShowDeleteConfirm(true);
    };

    const closeDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setLicencaToDelete(null);
    };

    const deleteLicenca = async () => {
        if (!licencaToDelete) return;
        setDeleting(true);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showTempToast('Erro', 'Sessão expirada', 'danger');
                setDeleting(false);
                return;
            }

            await axios.delete(
                `${API_BASE_URL}/licencas/${licencaToDelete._id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            showTempToast('Sucesso', 'Licença apagada com sucesso!', 'success');
            closeDeleteConfirm();
            await fetchLicencas();

        } catch (err) {
            console.error('❌ Erro ao apagar licença:', err);
            const msg = err.response?.data?.erro || err.response?.data?.message || 'Erro ao apagar licença';
            showTempToast('Erro', msg, 'danger');
        } finally {
            setDeleting(false);
        }
    };

    // ==============================================
    // GERAR NOVA CHAVE
    // ==============================================
    const gerarNovaChave = () => {
        setFormData({ ...formData, chave: generateLicencaKey() });
        showTempToast('Sucesso', 'Nova chave gerada!', 'success');
    };

    // ==============================================
    // RENDER TOAST
    // ==============================================
    const renderToast = () => {
        if (!tempToast) return null;
        return (
            <div className={`licencas-toast ${tempToast.type}`}>
                <div className="toast-icon">
                    <Icon name={tempToast.type === 'success' ? 'check_circle' : 'error'} size={20} />
                </div>
                <div className="toast-content">
                    <div className="toast-title">{tempToast.title}</div>
                    <div className="toast-message">
                        {typeof tempToast.message === 'string' ? tempToast.message : 'Erro inesperado'}
                    </div>
                </div>
                <button className="toast-close" onClick={() => setTempToast(null)}>×</button>
            </div>
        );
    };

    // ==============================================
// RENDER MODAL DE RENOVAÇÃO
// ==============================================
const renderRenovarModal = () => {
    if (!showRenovarModal || !licencaToRenovar) return null;
    const empresaNome = getEmpresaNome(licencaToRenovar);
    const statusAtual = getStatusLabel(licencaToRenovar);
    const isExpirada = statusAtual === 'Expirada';

    return (
        <div className="licencas-modal-overlay" onClick={closeRenovarModal}>
            <div className="licencas-modal licencas-renovar-modal" onClick={(e) => e.stopPropagation()}>
                <div className="licencas-modal-header">
                    <h3>🔄 Renovar Licença</h3>
                    <button className="licencas-modal-close" onClick={closeRenovarModal}>×</button>
                </div>
                <div className="licencas-modal-body">
                    <div className="licencas-info-block" style={{
                        marginBottom: '1rem',
                        padding: '0.75rem',
                        background: isExpirada ? 'rgba(220, 38, 38, 0.08)' : 'var(--color-bg-input)',
                        borderRadius: '8px',
                        border: isExpirada ? '1px solid rgba(220, 38, 38, 0.2)' : 'none'
                    }}>
                        {isExpirada && (
                            <p className="licencas-alerta-expirada" style={{ color: 'var(--color-danger)', fontWeight: 600, marginBottom: '0.5rem' }}>
                                ⚠️ Esta licença está EXPIRADA. A renovação irá reativá-la.
                            </p>
                        )}
                        <p><strong>Empresa:</strong> {empresaNome}</p>
                        <p><strong>Chave:</strong> <span className="licencas-chave-text" style={{ fontFamily: 'monospace' }}>{licencaToRenovar.chave}</span></p>
                        <p><strong>Plano:</strong> {licencaToRenovar.plano}</p>
                        <p><strong>Status Atual:</strong> <span className={`licencas-status-badge ${getStatusBadgeClass(licencaToRenovar)}`}>{statusAtual}</span></p>
                        {licencaToRenovar.dataExpiracao && (
                            <p><strong>Expirou em:</strong> {formatDate(licencaToRenovar.dataExpiracao)}</p>
                        )}
                        {licencaToRenovar.dataExpiracaoAnterior && (
                            <p className="licencas-info-anterior" style={{ fontSize: '0.7rem', color: 'var(--color-info-dark)', marginTop: '0.3rem' }}>
                                <em>Expiração anterior à última renovação: {formatDate(licencaToRenovar.dataExpiracaoAnterior)}</em>
                            </p>
                        )}
                    </div>

                    <div className="licencas-form-group">
                        <label>Período de Renovação</label>
                        <select
                            value={renovacaoData.periodo}
                            onChange={(e) => setRenovacaoData({ ...renovacaoData, periodo: e.target.value })}
                        >
                            <option value="Mensal">📆 Mensal (30 dias)</option>
                            <option value="Anual">📅 Anual (365 dias)</option>
                            <option value="Custom">⚙️ Personalizado</option>
                        </select>
                    </div>

                    {renovacaoData.periodo === 'Custom' && (
                        <div className="licencas-form-group">
                            <label>Dias a adicionar</label>
                            <input
                                type="number"
                                min="1"
                                max="365"
                                value={renovacaoData.dias}
                                onChange={(e) => setRenovacaoData({ ...renovacaoData, dias: Number(e.target.value) || 30 })}
                            />
                        </div>
                    )}

                    <div className="licencas-info-box" style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(255, 193, 7, 0.1)', borderRadius: '8px', border: '1px solid rgba(255, 193, 7, 0.2)' }}>
                        <Icon name="info" size={14} style={{ marginRight: '0.5rem' }} />
                        <span className="licencas-info-text" style={{ fontSize: '0.7rem', color: 'var(--color-info-dark)' }}>
                            A renovação irá estender a data de expiração a partir da data atual.
                            {isExpirada && ' A licença será REATIVADA automaticamente.'}
                        </span>
                    </div>
                </div>
                <div className="licencas-modal-footer" style={{ justifyContent: 'center' }}>
                    <button className="licencas-btn-cancel" onClick={closeRenovarModal} disabled={renovando}>
                        Cancelar
                    </button>
                    <button className="licencas-btn-renovar" onClick={renovarLicenca} disabled={renovando}>
                        {renovando ? (
                            <>⏳ A renovar...</>
                        ) : (
                            <>Renovar Licença</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// RENDER MODAL DE CONFIRMAÇÃO DE EXCLUSÃO
// ==============================================
const renderDeleteConfirmModal = () => {
    if (!showDeleteConfirm || !licencaToDelete) return null;
    const empresaNome = getEmpresaNome(licencaToDelete);
    const statusAtual = getStatusLabel(licencaToDelete);

    return (
        <div className="licencas-modal-overlay" onClick={closeDeleteConfirm}>
            <div className="licencas-modal licencas-delete-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="licencas-modal-header">
                    <h3>🗑️ Apagar Licença</h3>
                    <button className="licencas-modal-close" onClick={closeDeleteConfirm}>×</button>
                </div>
                <div className="licencas-modal-body licencas-modal-body-center" style={{ textAlign: 'center' }}>
                    <div className="licencas-delete-icon" style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                    <h4 className="licencas-delete-titulo">Tem certeza que deseja apagar esta licença?</h4>
                    <p className="licencas-delete-info" style={{ color: 'var(--color-info-dark)' }}>
                        <strong>Empresa:</strong> {empresaNome}<br />
                        <strong>Chave:</strong> {licencaToDelete.chave}<br />
                        <strong>Plano:</strong> {licencaToDelete.plano}<br />
                        <strong>Limite:</strong> {licencaToDelete.limiteUtilizadores} utilizadores<br />
                        <strong>Status Atual:</strong> {statusAtual}
                    </p>
                    <p className="licencas-delete-aviso" style={{ color: 'var(--color-danger)', fontSize: '0.85rem' }}>
                        Esta ação não pode ser desfeita!
                    </p>
                </div>
                <div className="licencas-modal-footer" style={{ justifyContent: 'center' }}>
                    <button className="licencas-btn-cancel" onClick={closeDeleteConfirm} disabled={deleting}>
                        Cancelar
                    </button>
                    <button className="licencas-btn-delete" onClick={deleteLicenca} disabled={deleting}>
                        {deleting ? 'A apagar...' : 'Apagar Licença'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// RENDER MODAL DE LICENÇA
// ==============================================
const renderModal = () => {
    if (!showModal) return null;

    const isAdmin = loggedUser?.perfil === 'Admin';
    const isSuper = loggedUser?.perfil === 'Superadmin';

    const empresasDisponiveis = isSuper ? empresas :
        empresas.filter(e => e._id === extrairEmpresaId(loggedUser?.empresaId));

    const empresaSelecionada = empresas.find(e => e._id === formData.empresaId);
    let nomeEmpresaSelecionada = '';
    if (empresaSelecionada) {
        nomeEmpresaSelecionada = empresaSelecionada.nome;
    } else if (isEditing && formData.empresaId) {
        const found = empresas.find(e => e._id === formData.empresaId);
        if (found) {
            nomeEmpresaSelecionada = found.nome;
        }
    }

    return (
        <div className="licencas-modal-overlay" onClick={closeModal}>
            <div className="licencas-modal" onClick={(e) => e.stopPropagation()}>
                <div className="licencas-modal-header">
                    <h3>{isEditing ? '✏️ Editar Licença' : '➕ Nova Licença'}</h3>
                    <button className="licencas-modal-close" onClick={closeModal}>×</button>
                </div>
                <div className="licencas-modal-body">
                    {/* Empresa */}
                    <div className="licencas-form-group">
                        <label>Empresa *</label>
                        <select
                            value={formData.empresaId}
                            onChange={(e) => {
                                const empresaId = e.target.value;
                                setFormData({
                                    ...formData,
                                    empresaId: empresaId
                                });
                            }}
                            disabled={isAdmin || isEditing}
                            className="licencas-empresa-select"
                        >
                            <option value="">Selecione uma empresa</option>
                            {empresasDisponiveis.map(emp => (
                                <option key={emp._id} value={emp._id}>
                                    {emp.nome} {emp.nomeCurto ? `(${emp.nomeCurto})` : ''}
                                </option>
                            ))}
                        </select>

                        {isEditing && formData.empresaId && (
                            <small className="licencas-empresa-info" style={{
                                color: nomeEmpresaSelecionada ? 'var(--color-success)' : 'var(--color-warning)',
                                display: 'block',
                                marginTop: '0.3rem'
                            }}>
                                {nomeEmpresaSelecionada ? (
                                    <>✅ Empresa: <strong>{nomeEmpresaSelecionada}</strong></>
                                ) : (
                                    <>⚠️ Empresa não encontrada na lista (ID: {formData.empresaId})</>
                                )}
                            </small>
                        )}
                        {isAdmin && (
                            <small className="licencas-empresa-info-auto" style={{ color: 'var(--color-info-dark)' }}>
                                A empresa é definida automaticamente para o seu perfil
                            </small>
                        )}
                    </div>

                    {/* Chave da Licença */}
                    <div className="licencas-form-group">
                        <label>Chave da Licença *</label>
                        <div className="licencas-chave-input-group">
                            <input
                                type="text"
                                value={formData.chave}
                                onChange={(e) => setFormData({ ...formData, chave: e.target.value })}
                                className="licencas-chave-input"
                                style={{ flex: 1, fontFamily: 'monospace', letterSpacing: '0.5px' }}
                                required
                            />
                            <button
                                type="button"
                                className="licencas-btn-gerar-chave"
                                onClick={gerarNovaChave}
                            >
                                <Icon name="refresh" size={14} /> Nova
                            </button>
                        </div>
                    </div>

                    {/* Plano e Tipo de Prazo */}
                    <div className="licencas-form-row">
                        <div className="licencas-form-group">
                            <label>Plano</label>
                            <select
                                value={formData.plano}
                                onChange={(e) => setFormData({ ...formData, plano: e.target.value })}
                            >
                                <option value="Trial">Trial</option>
                                <option value="Basico">Básico</option>
                                <option value="Profissional">Profissional</option>
                                <option value="Enterprise">Enterprise</option>
                                <option value="Custom">Custom</option>
                            </select>
                        </div>
                        <div className="licencas-form-group">
                            <label>Tipo de Prazo *</label>
                            <select
                                value={formData.tipoPrazo}
                                onChange={(e) => setFormData({ ...formData, tipoPrazo: e.target.value })}
                            >
                                <option value="Infinito">♾️ Infinito</option>
                                <option value="DataFim">📅 Data Específica</option>
                                <option value="Mensal">📆 Mensal</option>
                                <option value="Anual">📅 Anual</option>
                                <option value="Desativada">🔴 Desativada</option>
                            </select>
                        </div>
                    </div>

                    {/* Datas */}
                    <div className="licencas-form-row">
                        <div className="licencas-form-group">
                            <label>Data de Início *</label>
                            <input
                                type="date"
                                value={formData.dataInicio}
                                onChange={(e) => setFormData({ ...formData, dataInicio: e.target.value })}
                                required
                            />
                        </div>
                        <div className="licencas-form-group">
                            <label>Data de Fim</label>
                            <input
                                type="date"
                                value={formData.dataFim}
                                onChange={(e) => setFormData({ ...formData, dataFim: e.target.value })}
                                disabled={formData.tipoPrazo !== 'DataFim'}
                                required={formData.tipoPrazo === 'DataFim'}
                            />
                            {formData.tipoPrazo === 'Infinito' && (
                                <small className="licencas-form-hint" style={{ color: 'var(--color-info-dark)' }}>Licença vitalícia - sem data de expiração</small>
                            )}
                            {formData.tipoPrazo === 'Desativada' && (
                                <small className="licencas-form-hint licencas-form-hint-danger" style={{ color: 'var(--color-danger)' }}>Licença desativada</small>
                            )}
                        </div>
                    </div>

                    {/* Limite de Utilizadores */}
                    <div className="licencas-form-group">
                        <label>Limite de Utilizadores *</label>
                        <input
                            type="number"
                            min="1"
                            max="999"
                            value={formData.limiteUtilizadores}
                            onChange={(e) => setFormData({ ...formData, limiteUtilizadores: Number(e.target.value) || 1 })}
                            required
                        />
                    </div>

                    {/* Checkbox Licença Ativa */}
                    <div className="licencas-form-group licencas-checkbox-group">
                        <label className="licencas-checkbox-label">
                            <input
                                type="checkbox"
                                checked={formData.ativa}
                                onChange={(e) => setFormData({ ...formData, ativa: e.target.checked })}
                            />
                            <span>Licença Ativa</span>
                        </label>
                    </div>
                </div>
                <div className="licencas-modal-footer">
                    <button className="licencas-btn-cancel" onClick={closeModal}>Cancelar</button>
                    <button className="licencas-btn-save" onClick={saveLicenca}>
                        {isEditing ? 'Atualizar' : 'Criar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

    // ==============================================
    // RENDER PAGINAÇÃO
    // ==============================================
    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const startItem = (currentPage - 1) * itemsPerPage + 1;
        const endItem = Math.min(currentPage * itemsPerPage, filteredLicencas.length);

        return (
            <div className="licencas-pagination">
                <div className="pagination-info">
                    <span>
                        Mostrando {startItem} - {endItem} de {filteredLicencas.length} licenças
                    </span>
                </div>
                <div className="pagination-controls">
                    <button
                        className="pagination-btn"
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                    >
                        <Icon name="chevron_left" size={18} />
                    </button>

                    <div className="pagination-numbers">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                            if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 2 && page <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={page}
                                        className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                        onClick={() => goToPage(page)}
                                    >
                                        {page}
                                    </button>
                                );
                            }
                            if (
                                (page === currentPage - 3 && page > 2) ||
                                (page === currentPage + 3 && page < totalPages - 1)
                            ) {
                                return <span key={page} className="pagination-ellipsis">…</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        className="pagination-btn"
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                    >
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // VERIFICAR PERMISSÃO
    // ==============================================
    const podeVer = loggedUser?.perfil === 'Superadmin' || loggedUser?.perfil === 'Admin';

    if (!loggedUser) {
        return (
            <div className="licencas-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>A carregar...</p>
                </div>
            </div>
        );
    }

    if (!podeVer) {
        return (
            <div className="licencas-container">
                <div className="access-denied">
                    <Icon name="lock" size={64} color="var(--color-danger)" />
                    <h2>Acesso Negado</h2>
                    <p>Não tem permissão para visualizar as licenças.</p>
                    <p style={{ fontSize: '0.85rem', color: 'var(--color-info-dark)' }}>
                        Apenas Administradores e Superadmins podem aceder a esta área.
                    </p>
                </div>
            </div>
        );
    }

    const currentLicencas = getCurrentPageLicencas();

    return (
        <div className="licencas-container">
            {/* HEADER */}
            <div className="licencas-header">
                <div className="header-title">
                    <h2>📜 Gestão de Licenças</h2>
                    <p>Gerencie as licenças das empresas do sistema</p>
                </div>
                <button className="btn-add" onClick={openAddModal}>
                    <Icon name="add" size={20} /> Nova Licença
                </button>
            </div>

            {/* STATS */}
            <div className="licencas-stats">
                <div className="stat-card">
                    <div className="stat-icon total">
                        <Icon name="receipt_long" size={24} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.total}</div>
                        <div className="stat-label">Total</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon active">
                        <Icon name="check_circle" size={24} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.ativas}</div>
                        <div className="stat-label">Ativas</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon warning">
                        <Icon name="warning" size={24} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.pendentes}</div>
                        <div className="stat-label">A expirar</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon expired">
                        <Icon name="cancel" size={24} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.expiradas}</div>
                        <div className="stat-label">Expiradas</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon inactive">
                        <Icon name="block" size={24} />
                    </div>
                    <div className="stat-info">
                        <div className="stat-value">{stats.inativas}</div>
                        <div className="stat-label">Inativas</div>
                    </div>
                </div>
                {stats.empresa_inexistente > 0 && (
                    <div className="stat-card">
                        <div className="stat-icon empresa-inexistente">
                            <Icon name="warning" size={24} />
                        </div>
                        <div className="stat-info">
                            <div className="stat-value">{stats.empresa_inexistente}</div>
                            <div className="stat-label">Empresa Inexistente</div>
                        </div>
                    </div>
                )}
                {stats.sem_licenca > 0 && (
                    <div className="stat-card">
                        <div className="stat-icon sem-licenca">
                            <Icon name="ticket" size={24} />
                        </div>
                        <div className="stat-info">
                            <div className="stat-value">{stats.sem_licenca}</div>
                            <div className="stat-label">Sem Licença</div>
                        </div>
                    </div>
                )}
            </div>

            {/* SEARCH */}
            <div className="licencas-search">
                <div className="search-wrapper">
                    <Icon name="search" size={20} color="var(--color-info-dark)" />
                    <input
                        type="text"
                        placeholder="Pesquisar por empresa, chave ou plano..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clear-search" onClick={() => setSearchTerm('')}>×</button>
                    )}
                </div>
            </div>

            {/* TABLE */}
            <div className="licencas-table-wrapper">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>A carregar licenças...</p>
                    </div>
                ) : filteredLicencas.length === 0 ? (
                    <div className="empty-state">
                        <Icon name="ticket" size={48} color="var(--color-info-dark)" />
                        <h3>Nenhuma licença encontrada</h3>
                        <p>{searchTerm ? 'Tente ajustar os filtros de pesquisa' : 'Crie a primeira licença'}</p>
                    </div>
                ) : (
                    <>
                        <table className="licencas-table">
                            <thead>
                                <tr>
                                    <th>EMPRESA</th>
                                    <th>CHAVE</th>
                                    <th>PLANO</th>
                                    <th>TIPO</th>
                                    <th>LIMITE</th>
                                    <th>PERÍODO</th>
                                    <th>STATUS</th>
                                    <th>AÇÕES</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentLicencas.map((lic, index) => {
                                    const empresaNome = getEmpresaNome(lic);
                                    const status = getStatusLabel(lic);
                                    const statusClass = getStatusBadgeClass(lic);
                                    const isAdmin = loggedUser?.perfil === 'Admin';
                                    const podeEditar = isSuperadmin || (isAdmin && !lic.empresa?.nome);
                                    const dataExpiracao = lic.dataExpiracao || lic.dataFim;
                                    const isExpirada = status === 'Expirada';
                                    const isEmpresaInexistente = status === 'Empresa Inexistente';
                                    const isInativa = status === 'Inativa' || status === 'Desativada' || status === 'Empresa Arquivada' || status === 'Empresa Inativa';
                                    const isArquivada = status === 'Arquivada' || status === 'Empresa Arquivada' || status === 'Empresa Inexistente';

                                    let rowClass = '';
                                    if (isEmpresaInexistente) rowClass = 'licenca-empresa-inexistente';
                                    else if (isArquivada) rowClass = 'licenca-arquivada';
                                    else if (isExpirada) rowClass = 'licenca-expirada';
                                    else if (isInativa) rowClass = 'licenca-inativa';

                                    return (
                                        <tr key={lic._id || index} className={rowClass}>
                                            <td>
                                                <div className="empresa-cell">
                                                    <span className="empresa-nome">{empresaNome}</span>
                                                    {lic.empresa?.nomeCurto && (
                                                        <span className="empresa-curto">{lic.empresa.nomeCurto}</span>
                                                    )}
                                                    {isEmpresaInexistente && (
                                                        <span className="empresa-inexistente-badge">
                                                            <Icon name="warning" size={12} /> Empresa não existe
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="chave-cell" style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                    {lic.chave || '-'}
                                                </span>
                                            </td>
                                            <td>{getPlanoBadge(lic.plano)}</td>
                                            <td>
                                                <span className="tipo-badge">
                                                    {getTipoPrazoLabel(lic.tipoPrazo)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="limite-cell">
                                                    <span className="limite-usuarios">{lic.limiteUtilizadores}</span>
                                                    <span className="limite-label">utilizador(es)</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="periodo-cell">
                                                    <span className="data-inicio">
                                                        <Icon name="event_available" size={10} /> {formatDate(lic.dataInicio)}
                                                    </span>
                                                    {dataExpiracao && (
                                                        <span className={`data-fim ${isExpirada ? 'expirada' : ''}`}>
                                                            <Icon name="event_busy" size={10} /> {formatDate(dataExpiracao)}
                                                        </span>
                                                    )}
                                                    {!dataExpiracao && lic.tipoPrazo !== 'Infinito' && (
                                                        <span className="data-fim">
                                                            <Icon name="event_busy" size={10} /> -
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${statusClass}`}>
                                                    {status}
                                                    {status === 'Ativa (∞)' && <span className="infinito-icon">∞</span>}
                                                </span>
                                                {lic.motivoInativacao && lic.motivoInativacao !== 'Expirada' && (
                                                    <span style={{ display: 'block', fontSize: '0.55rem', color: 'var(--color-info-dark)', marginTop: '0.2rem' }}>
                                                        {lic.motivoInativacao}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="actions-cell">
                                                {/* Botão renovar — aparece para licenças não-ininfinitas e válidas */}
                                                {lic.tipoPrazo !== 'Infinito' && !isArquivada && !isEmpresaInexistente && (
                                                    <button
                                                        className="action-btn renovar"
                                                        onClick={() => openRenovarModal(lic)}
                                                        title="Renovar licença"
                                                        disabled={!podeEditar}
                                                    >
                                                        <Icon name="refresh" size={16} />
                                                    </button>
                                                )}

                                                {/* Botão editar */}
                                                {!isArquivada && !isEmpresaInexistente && (
                                                    <button
                                                        className="action-btn edit"
                                                        onClick={() => openEditModal(lic)}
                                                        title="Editar licença"
                                                        disabled={!podeEditar}
                                                    >
                                                        <Icon name="edit" size={16} />
                                                    </button>
                                                )}

                                                {/* Botão ativar/desativar */}
                                                {!isArquivada && !isEmpresaInexistente && (
                                                    <button
                                                        className={`action-btn toggle ${lic.ativa ? '' : 'inactive'}`}
                                                        onClick={() => toggleLicencaStatus(lic)}
                                                        title={lic.ativa ? 'Desativar licença' : 'Ativar licença'}
                                                        disabled={!podeEditar}
                                                    >
                                                        <Icon name={lic.ativa ? 'lock_open' : 'lock'} size={16} />
                                                    </button>
                                                )}

                                                {/* Botão apagar */}
                                                <button
                                                    className="action-btn delete"
                                                    onClick={() => confirmDelete(lic)}
                                                    title="Apagar licença"
                                                    disabled={!podeEditar}
                                                >
                                                    <Icon name="delete" size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {renderPagination()}
                    </>
                )}
            </div>

            {/* MODALS */}
            {renderModal()}
            {renderRenovarModal()}
            {renderDeleteConfirmModal()}
            {renderToast()}
        </div>
    );
}